import React, { useCallback, useEffect, useState } from 'react'
import { CBadge, CSmartTable } from '@coreui/react-pro'
import moment from 'moment'
import EmailQuotaService from 'src/service/EmailQuotaService'
import './GameNotification.css'

// The API groups each send by timestamp, so one row is one send: when it went out, how many
// members it reached and who they were. Only created_at and the email_count alias are real
// sort keys — the rest are rendered client-side.
const columns = [
  { label: '#', key: 'sn', filter: false, sorter: false, _style: { width: '5%' } },
  { label: 'Sent', key: 'created_at', filter: false, sorter: true, _style: { width: '16%' } },
  { label: 'Time', key: 'send_time', filter: false, sorter: false, _style: { width: '12%' } },
  {
    label: 'Recipients',
    key: 'email_count',
    filter: false,
    sorter: true,
    _style: { width: '12%' },
  },
  { label: 'Sent to', key: 'sent_to', filter: false, sorter: false },
]

const FantasyRoundEmailLog = ({ emailType }) => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnSorter, setColumnSorter] = useState({})

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      const offset = itemsPerPage * (activePage - 1)
      const params = new URLSearchParams()
      if (columnSorter?.column) {
        params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
      }

      const result = await EmailQuotaService.getEmailLogs(
        emailType,
        offset,
        itemsPerPage,
        activePage,
        params,
      )
      setUsers(result.data)
    } catch (error) {
      console.error('Error fetching email logs:', error)
    } finally {
      setLoading(false)
    }
  }, [emailType, activePage, itemsPerPage, columnSorter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  return (
    <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
      <CSmartTable
        columns={columns}
        columnSorter={{ external: true }}
        noItemsLabel="This email has not been sent yet"
        scopedColumns={{
          // The row number has to carry the page offset, or every page restarts at 1.
          sn: (item, index) => (
            <td className="gn-num">{itemsPerPage * (activePage - 1) + index + 1}</td>
          ),
          created_at: (item) => (
            <td>{item.created_at ? moment(item.created_at).format('D.MM.YYYY') : '-'}</td>
          ),
          send_time: (item) => (
            <td>{item.created_at ? moment(item.created_at).format('h:mma') : '-'}</td>
          ),
          email_count: (item) => (
            <td>
              <CBadge color="primary" shape="rounded-pill" className="px-2 py-1">
                {item.email_count ?? 0}
              </CBadge>
            </td>
          ),
          sent_to: (item) => (
            <td>
              <span className="gn-sent-to" title={item.sent_to || ''}>
                {item.sent_to || '—'}
              </span>
            </td>
          ),
        }}
        items={users?.data}
        itemsPerPage={itemsPerPage}
        itemsPerPageSelect
        loading={loading}
        pagination={{ external: true }}
        paginationProps={{
          activePage,
          pages: Math.ceil(users?.total / itemsPerPage) || 1,
        }}
        tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
        onActivePageChange={(page) => setActivePage(page)}
        onItemsPerPageChange={(perPage) => {
          setActivePage(1)
          setItemsPerPage(perPage)
        }}
        onSorterChange={(sorter) => setColumnSorter(sorter)}
      />
    </div>
  )
}

export default FantasyRoundEmailLog
